import React, { useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router';
import { ELeaderboardTabsValues, ERoutes } from '../../enums';
import { ScreenWrapper } from '../../components/Wrappers/ScreenWrapper';
import { useTranslation } from 'react-i18next';
import { TMaterialStyles } from '../../theme';
import TypographyWithShadow from '../../components/Typography/TypographyWithShadow';
import { Icon } from '../../components/Icon';
import { TabContext } from '@mui/lab';
import { CustomTabs, TTab } from '../../components/Tabs/Tabs';
import i18next from 'i18next';
import _ from 'lodash';
import { CustomTabPanel } from '../../components/Tabs/TabPanel';
import { getTotalLeaderboard, getWeeklyLeaderboard } from '../../queries';
import { ButtonWithShadow } from '../../components/Buttons/ButtonWithShadow';
import { LeaderboardUserItem } from '../../components/Items/LeaderboardUserItem';
import { useLeaderboardStore } from '../../stores';

const LEADERBOARD_TABS: TTab[] = [
  {
    label: i18next.t('leaderboards.competition'),
    value: ELeaderboardTabsValues.weekly,
  },
  {
    label: i18next.t('leaderboards.general'),
    value: ELeaderboardTabsValues.total,
  },
];

const LeaderboardScreen: React.FC = () => {
  const { weekly, total } = useLeaderboardStore();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState<string>(
    ELeaderboardTabsValues.weekly,
  );

  const onBack = useCallback(() => navigate(ERoutes.main), [navigate]);

  const onChangeTabValue = useCallback(
    (_e: any, value: string) => setTabValue(value),
    [],
  );

  const loadLeaderboard = useCallback(async () => {
    try {
      switch (tabValue) {
        case ELeaderboardTabsValues.weekly:
          getWeeklyLeaderboard();
          break;
        case ELeaderboardTabsValues.total:
          getTotalLeaderboard();
          break;
      }
    } catch (error: any) {
      console.error('Error while [loadLeaderboard]', error);
    }
  }, [tabValue]);

  useEffect(() => {
    loadLeaderboard();
  }, [loadLeaderboard]);

  return (
    <ScreenWrapper onBack={onBack}>
      <Box sx={materialStyles.screen}>
        <Box sx={materialStyles.leaderboardContainer}>
          <Icon
            iconName="LeaderBoardIcon"
            sx={materialStyles.leaderboardIcon}
          />
        </Box>
        <TypographyWithShadow sx={materialStyles.leaderboardScreenTitle}>
          {t('leaderboards.leaderboard')}
        </TypographyWithShadow>
        <TabContext value={tabValue}>
          <CustomTabs
            onChange={onChangeTabValue}
            tabs={LEADERBOARD_TABS}
            sx={materialStyles.tabsContainer}
          />
          <ButtonWithShadow disabled sx={materialStyles.totalUsersContainer}>
            <TypographyWithShadow
              shadowType="DARK"
              sx={materialStyles.totalUsersText}
            >
              {t('leaderboards.total')}
            </TypographyWithShadow>
            <TypographyWithShadow
              shadowType="DARK"
              sx={materialStyles.totalUsersText}
            >
              {t('leaderboards.users_with_count', {
                value: weekly?.totalUsers || total?.totalUsers,
              })}
            </TypographyWithShadow>
          </ButtonWithShadow>
          <CustomTabPanel
            sx={materialStyles.tabPanelContainer}
            value={ELeaderboardTabsValues.weekly}
          >
            <TypographyWithShadow sx={materialStyles.description}>
              {t('leaderboards.competition_leaderboard_tracks_hint')}
            </TypographyWithShadow>
            {weekly?.currentUser ? (
              <LeaderboardUserItem
                user={weekly.currentUser}
                type="CURRENT_USER"
              />
            ) : null}
            <Box sx={materialStyles.leaderboardListContainer}>
              {_.map(weekly?.topPlayers, (user, index) => (
                <LeaderboardUserItem key={`${index}`} user={user} />
              ))}
            </Box>
          </CustomTabPanel>
          <CustomTabPanel
            sx={materialStyles.tabPanelContainer}
            value={ELeaderboardTabsValues.total}
          >
            <TypographyWithShadow sx={materialStyles.description}>
              {t('leaderboards.general_leaderboard_tracks_hint')}
            </TypographyWithShadow>
            {total?.currentUser ? (
              <LeaderboardUserItem
                user={total.currentUser}
                type="CURRENT_USER"
              />
            ) : null}
            <Box sx={materialStyles.leaderboardListContainer}>
              {_.map(total?.topPlayers, (user, index) => (
                <LeaderboardUserItem key={`${index}`} user={user} />
              ))}
            </Box>
          </CustomTabPanel>
        </TabContext>
      </Box>
    </ScreenWrapper>
  );
};

const materialStyles: TMaterialStyles = {
  screen: {
    pt: '24px',
    px: '18px',
    overflow: 'auto',
  },
  leaderboardContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  description: {
    mb: '16px',
    textTransform: 'uppercase',
    fontSize: '15px',
    lineHeight: '100%',
    textAlign: 'center',
    fontWeight: '600',
  },
  leaderboardIcon: {
    width: '149px',
    height: '124px',
  },
  leaderboardScreenTitle: {
    fontFamily: 'Rubber Duck !important',
    textAlign: 'center',
    fontSize: '44px',
    lineHeight: '100%',
    textTransform: 'uppercase',
  },
  tabsContainer: {
    marginTop: '17px',
  },
  totalUsersContainer: {
    px: '17px',
    py: '9px',
    borderRadius: '14px',
    width: '100%',
    mt: '12px',
    mb: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  tabPanelContainer: {
    width: '100%',
    pb: '16px',
  },
  totalUsersText: {
    textTransform: 'uppercase',
    fontWeight: 600,
    fontSize: '15px',
    lineHeight: '100%',
  },
  leaderboardListContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    width: '100%',
    mt: '29px',
  },
};

export default LeaderboardScreen;
